import { useQuery } from "@tanstack/react-query";
import { ProviderCountData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { ProviderOverlapData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/ProviderOverlapData";
import { BenchmarkingServiceType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/utility";

export interface ProviderOverlapDataResult {
  providerOverlapData: ProviderOverlapData | null;
  providerOverlapDataLoading: boolean;
}

export const PROVIDER_OVERLAP_DATA_QUERY_KEY_PREFIX = "benchmarking-provider-overlap-data";

export const useProviderOverlapData = (
  service: BenchmarkingServiceType,
  payload: NetworkComparisonFiltersPayload | null
): ProviderOverlapDataResult => {
  const queryResult = useQuery<ProviderOverlapData>({
    enabled: !!payload,
    // provider overlap data api call retrieves data for the following graphs:
    // 1.) provider overlap
    // 2.) provider count: in network
    // 3.) provider count: out of network
    queryFn: async ({ signal }) => {
      const data = await service.getProviderOverlapData(payload, signal);

      if (!!data) {
        // unique bar is built off of in primary only count
        const uniqueCount: ProviderCountData = {
          count: data.inPrimaryOnlyCount,
          inNetwork: true,
          inOtherNetworkCount: "Unique",
        };

        data.inNetworkCounts = [uniqueCount, ...data.inNetworkCounts];
      }

      return data;
    },
    queryKey: [PROVIDER_OVERLAP_DATA_QUERY_KEY_PREFIX, service?.lineOfBusiness, JSON.stringify(payload)],
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    providerOverlapData: queryResult.data ?? null,
    providerOverlapDataLoading: queryResult.isFetching,
  };
}

export default useProviderOverlapData;
